'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */
import classNames, { Argument as classNamesArg } from 'classnames';
import { ReactNode, useRef } from 'react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SwiperOptions } from 'swiper/types';
import ChevronRight from '../icons/ChevronRight';
import { MaterialIcon } from '../MaterialIcon';
// import 'swiper/swiper-bundle.css'; // moved to global cms-bundle.css

type SwiperProps = {
  children: ReactNode[];
  navigationClassNames?: classNamesArg;
  navigationWrapperClassNames?: classNamesArg;
  slideClassNames?: classNamesArg;
  swiperClassNames?: classNamesArg;
  swiperWrapperClassNames?: classNamesArg;
  swiperNestedClassNames?: classNamesArg;
  swiperNestedWrapperClassNames?: classNamesArg;
  swiperPropsOverride?: SwiperOptions;
  swiperButtonNextClassNames?: classNamesArg;
  swiperButtonPrevClassNames?: classNamesArg;
  showNavigationButton?: boolean;
  isDarkTheme?: boolean;
  swiperCustomPagination?: boolean;
  swiperCustomNavigation?: boolean;
};

const defaultSwiperProps = {
  centeredSlides: true,
  loop: false,
  spaceBetween: 24,
  modules: [Navigation, Pagination, Autoplay],
  navigation: {
    nextEl: '.swiper-button-next',
    prevEl: '.swiper-button-prev',
    disabledClass: '!hidden !cursor-not-allowed',
  },
};

export function FeCarousel({
  children,
  navigationClassNames = '',
  navigationWrapperClassNames = '',
  slideClassNames = '',
  swiperClassNames = '',
  swiperWrapperClassNames = '',
  swiperPropsOverride = {},
  swiperButtonNextClassNames = '',
  swiperNestedClassNames = '',
  swiperNestedWrapperClassNames = '',
  swiperButtonPrevClassNames = '',
  showNavigationButton = false,
  isDarkTheme = false,
  swiperCustomPagination = false,
  swiperCustomNavigation = false,
}: SwiperProps): JSX.Element {
  const defaultNavigationClassNames =
    '!size-[24px] !top-[22px] !h-[100%] bg-white after:!content-none';

  const prevElRef = useRef<HTMLDivElement>(null);
  const nextElRef = useRef<HTMLDivElement>(null);
  const paginationElRef = useRef<HTMLDivElement>(null);

  const swiperProps: SwiperOptions = {
    ...defaultSwiperProps,
    ...swiperPropsOverride,
  };

  if (swiperCustomNavigation) {
    swiperProps.navigation = {
      prevEl: prevElRef.current,
      nextEl: nextElRef.current,
    };
  }
  if (swiperCustomPagination) {
    swiperProps.pagination = {
      el: paginationElRef.current,
      clickable: false,
    };
  }

  return (
    <div
      className={classNames(
        'fe-carousel',
        {
          'fe-carousel--dark': isDarkTheme,
          'fe-carousel--with-navigation': swiperCustomNavigation,
          'fe-carousel--with-pagination': swiperCustomPagination,
        },
        swiperClassNames,
        'group flex h-full flex-col transition',
      )}
    >
      <div
        className={classNames(
          swiperWrapperClassNames,
          'flex h-full grow items-center overflow-hidden',
        )}
      >
        {swiperCustomNavigation && (
          <>
            <div ref={prevElRef} className="swiper-button-custom-prev">
              <ChevronRight className="size-6" />
            </div>
            <div ref={nextElRef} className="swiper-button-custom-next">
              <ChevronRight className="size-6" />
            </div>
          </>
        )}
        {swiperCustomPagination && (
          <div ref={paginationElRef} className="swiper-pagination"></div>
        )}
        <Swiper
          className={classNames('size-full h-fit', swiperNestedClassNames)}
          wrapperClass={classNames('', swiperNestedWrapperClassNames)}
          {...swiperProps}
          enabled={swiperPropsOverride.enabled !== false ? true : false}
          onBeforeInit={(swiper) => {
            if (
              swiperCustomNavigation &&
              typeof swiper.params.navigation !== 'boolean' &&
              nextElRef.current !== null &&
              prevElRef.current !== null
            ) {
              swiper.params.navigation!.prevEl = prevElRef.current;
              swiper.params.navigation!.nextEl = nextElRef.current;
            }
            if (
              swiperCustomPagination &&
              paginationElRef.current !== null &&
              typeof swiper.params.pagination !== 'boolean'
            ) {
              swiper.params.pagination!.el = paginationElRef.current;
            }
          }}
        >
          {(children ?? []).map((child, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <SwiperSlide key={index} className={classNames(slideClassNames)}>
              {child}
            </SwiperSlide>
          ))}
          {showNavigationButton ? (
            <div
              className={classNames(
                navigationWrapperClassNames,
                'swiper-navigation-wrapper',
              )}
            >
              <div
                style={{
                  stroke: 'black !important',
                  fill: 'black !important',
                }}
                className={classNames(
                  defaultNavigationClassNames,
                  navigationClassNames,
                  swiperButtonPrevClassNames,
                  'swiper-button-prev !left-0',
                )}
              >
                <MaterialIcon
                  icon="expand_less"
                  px={24}
                  className="-rotate-90 text-black"
                />
              </div>
              <div
                style={{
                  stroke: 'black !important',
                  fill: 'black !important',
                }}
                className={classNames(
                  defaultNavigationClassNames,
                  navigationClassNames,
                  swiperButtonNextClassNames,
                  'swiper-button-next !right-0',
                )}
              >
                <MaterialIcon
                  icon="expand_less"
                  px={24}
                  className="rotate-90 text-black"
                />
              </div>
            </div>
          ) : null}
        </Swiper>
      </div>
    </div>
  );
}
